/**
 * accountMemberList.component.tsx
 * Sentriqs
 *
 * Created by: Dylan Knight
 * Copyright (c) 2022 Sentriqs, Inc. All rights reserved.
 *
**/

import React from "react";
import { Link } from "react-router-dom";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
//import EditIcon from "@mui/icons-material/Edit";
import MenuIcon from "@mui/icons-material/Menu";
import { Button, CircularProgress, IconButton, TextField } from "@mui/material"

import { AuthContext } from "../auth/auth.context";
import { AccountNavContext } from "./account.context";
import { AccountMemberDetailsComponent } from "./accountMemberDetail.component";
import { ErrorSnackBar } from "../components/errorSnackBar.component";
import { SCAvatar } from "../components/avatar.component";

import { BillingType, MemberRole, Membership } from "../models/auth";
import { authServiceFactory } from "../services/auth.service";
import { AccountBillSummaryComponent } from "../blofeld/billingView.component";

interface AccountMemberListContainerProps {
  updateMembership(userID: string, update: Partial<Membership>): Promise<Membership>,
}

export function AccountMemberListContainer(props: AccountMemberListContainerProps): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const acctContext = React.useContext<AccountNavContext>(AccountNavContext);
  const [errMsg, setErrMsg] = React.useState("");
  //const history = useHistory();
  const authService = authServiceFactory();

  /*React.useEffect(() => {
    if (!authContext.can(PermissionLevel.WRITE, "profiles")) {
      history.replace("/group");
    }
  });*/
  function getAccountMembers(filterText: string, limit: number): Promise<Array<Membership>> {
    return authService.getAccountMembers(filterText, limit);
  }

  function deleteMembership(userID: string): Promise<void> {
    return authService.deleteMembership(userID);
  }

  return <div className="account-member-list-container">
    <header className="group-header-nav attached-left-nav">
      <IconButton className="left-nav-outer-toggle" size="small"
          disableRipple disableTouchRipple
          onClick={acctContext.toggleLeftNav}>
        <MenuIcon />
      </IconButton>
      <h3>Members: {authContext.membership?.accountname || ""}</h3>
      <span className="right-controls">
        <Button size="small" component={Link} to="/admin/invite"
            disableRipple disableTouchRipple>
          <i className="fas fa-plus"></i>
          <span>&nbsp;Invite</span>
        </Button>
      </span>
    </header>
    <AccountMemberListComponent
      accountID={authContext.membership?.accountID || ""}
      getAccountMembers={getAccountMembers}
      updateMembership={props.updateMembership}
      deleteMembership={deleteMembership} />
  <ErrorSnackBar errMsg={errMsg} didClose={() => setErrMsg("") }/>
  </div>;
}


interface Props {
  accountID: string,
  compAvailable?: boolean,
  editBilling?: boolean,
  getAccountMembers(filterText: string, limit: number): Promise<Array<Membership>>,
  updateMembership(userID: string, update: Partial<Membership>): Promise<Membership>,
  deleteMembership(userID: string): Promise<void>
}

export function AccountMemberListComponent (props: Props): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const acctContext = React.useContext<AccountNavContext>(AccountNavContext);
  const [, setLoading] = React.useState(false);
  const [errMsg, setErrMsg] = React.useState("");
  const [members, setMembers] = React.useState<Array<Membership>>([]);
  const [filterText, setFilterText] = React.useState<string>("");
  const [timer, setTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [editSet, setEditSet] = React.useState<Set<number>>(new Set());

  const roleNameMap = {
    [MemberRole.admin]: "Admin",
    [MemberRole.manager]: "Manager",
    [MemberRole.user]: "User",
    [MemberRole.guest]: "Guest"
  };

  React.useEffect(function () {
    setFilterText("");
    props.getAccountMembers("", 100).then(mems => {
      setMembers(mems);
    }, err => {
      console.error("Error fetching members:", err);
      setErrMsg(err.message);
    });
  }, [props.accountID]);

  function searchValueChange(event: React.SyntheticEvent) {
    const value = (event.target as HTMLInputElement).value;
    setFilterText(value);
    if (timer) {
      clearTimeout(timer);
    }
    setTimer(setTimeout(async () => {
      try {
        setLoading(true);
        console.log("fetching members match:", value);
        const newMembers = await props.getAccountMembers(value, 100);
        setMembers(newMembers);
      } catch (err) {
        console.error("Error fetching members:", err);
        setErrMsg((err as Error).message);
      } finally {
        setLoading(false);
        setTimer(null);
      }
    }, 1000));
  }

  function startEditAtIndex(index: number) {
    const newEditing = new Set([...editSet, index]);
    setEditSet(newEditing);
  }

  async function updateMember(index: number, update: Partial<Membership>): Promise<Membership> {
    const member = members[index];
    try {
      if (update.memberrole && !update.billingType && member.billingType != BillingType.paid && 
          member.billingType != BillingType.guest) {
        // don't change billing type with role if the billing type is not one of the defaults
        update.billingType = member.billingType;
      }

      const m = await props.updateMembership(member.userID, update);
      const newMembers = Array.from(members);
      newMembers[index] = m;
      console.log("index:", index, "new member:", m);
      setMembers(newMembers);
      if (update.isComp && !m.isComp) {
        setErrMsg("Unable to assign complementary membership. All slots may have been allocated");
      }
      return m;
    } catch (err) {
      console.error("Error updating member:", err);
      setErrMsg((err as Error).message);
      return member;
    }
  }

  function finishEditAtIndex(index: number) {
    const newEditing = new Set(editSet);
    newEditing.delete(index);
    setEditSet(newEditing);
  }

  async function deleteMemberAtIndex(index: number): Promise<void> {
    try {
      const member = members[index];
      await props.deleteMembership(member.userID);
      const newMembers = Array.from(members);
      newMembers.splice(index, 1);
      setMembers(newMembers);
      const newEditing = new Set(editSet);
      newEditing.delete(index);
      setEditSet(newEditing);
    } catch (err) {
      console.error("Error deleting member:", err);
      setErrMsg((err as Error).message);
    }
  }

  return <>
    {props.editBilling && acctContext.currentAccount && <div>
      <AccountBillSummaryComponent 
        account={acctContext.currentAccount} 
        memberships={members.map((member) => {return {
          member: member,
          newBillingType: member.billingType,
          markedComp: !!member.isComp
        }})}/>
    </div>}
        
    <TextField className="member-search" label="Filter" variant="outlined" fullWidth
        size="small" value={filterText} onChange={searchValueChange} />

    {members.map((member, i) => {
      if (editSet.has(i)) {
        const editComp = member.isComp || props. compAvailable|| !!acctContext.currentAccount?.remainingFreeMemberships;
        return <MemberEditItem key={member.userID} member={member}
                index={i}
                editComp={editComp}
                editBilling={props.editBilling}
                roleNameMap={roleNameMap}
                updateMember={updateMember}
                onFinishEdit={finishEditAtIndex}
                deleteMember={deleteMemberAtIndex}
              />;
      }
      return <MemberListItem key={member.userID} member={member}
              displayTZ={authContext.tz()}
              roleName={roleNameMap[member.memberrole] || "User"}
              index={i}
              doEditAt={startEditAtIndex}
            />;
    })}

    <ErrorSnackBar errMsg={errMsg} didClose={() => setErrMsg("") }/>
  </>;
}

interface MemberProps {
  member: Membership,
  displayTZ: string,
  index: number,
  roleName: string,
  doEditAt(index: number): void
}

function MemberListItem(props: MemberProps): React.ReactElement<any, any> {
  if (props.displayTZ) {
    props.member.created.tz(props.displayTZ);
  }
  return <article className="account-member-list-item flex" onClick={() => props.doEditAt(props.index)}>
    <SCAvatar text={props.member.avatarText || "XX"} url={props.member.avatarUrl} />
    <div>
      <div className="member-name">{props.member.firstname} {props.member.lastname}</div>
      <div className="creation-time">Created: {props.member.created.format("MMM DD, YYYY h:mma")}</div>
    </div>
    <div className="right-controls">
      <span>{props.roleName}</span>
      <IconButton size="small" component={Link} to={`/admin/member/${props.member.userID}`}
            disableRipple disableTouchRipple>
        <ChevronRightIcon />
      </IconButton>
    </div>
  </article>;
  //<IconButton onClick={() => props.doEditAt(props.index)}><EditIcon /></IconButton>
}

interface MemberEditProps {
  member: Membership,
  index: number,
  editComp: boolean,
  editBilling?: boolean,
  roleNameMap: {[role: string]: string},
  updateMember(index: number, update: Partial<Membership>): Promise<Membership>,
  deleteMember(index: number): Promise<void>,
  onFinishEdit(index: number): void
}

function MemberEditItem(props: MemberEditProps): React.ReactElement<any, any> {  
  const [deleting, setDeleting] = React.useState<boolean>(false);

  async function deleteMember() {
    if (deleting) { return; }
    setDeleting(true);
    await props.deleteMember(props.index);
    setDeleting(false);
  }

  async function saveUpdate(newVal: Partial<Membership>): Promise<Membership> {
    const update: Partial<Membership> = newVal;
    update.userID = props.member.userID;

    let skipUpdate = true;
    if (update.firstname) {
      skipUpdate = false;
    }
    if (update.lastname) {
      skipUpdate = false;
    }
    if (update.memberrole) {
      skipUpdate = false;
    }
    if (update.isComp || update.isComp === false) {
      skipUpdate = false;
    }
    if (update.billingType) {
      skipUpdate = false;
    }
    if (skipUpdate) {
      console.log("skipping update, no changes to save");
      return props.member;
    }
    const m = await props.updateMember(props.index, update);
    return m;
  }

  return <article className="account-member-list-edit-item">
    <SCAvatar text={props.member.avatarText || "XX"} url={props.member.avatarUrl} />
    <div className="form-wrapper">
      <AccountMemberDetailsComponent member={props.member} 
        editComp={props.editComp} 
        editBilling={props.editBilling}
        saveUpdate={saveUpdate} />
      <div className="button-row">
        {deleting ?
          <CircularProgress size={20} color="secondary" /> :
          <Button variant="contained" color="secondary" className="delete-btn"
              disableRipple disableTouchRipple
              onClick={deleteMember}>
            Delete
          </Button>}
        <Button variant="contained" color="primary"
          disableRipple disableTouchRipple
          onClick={() => props.onFinishEdit(props.index)}>Done</Button>
      </div>
    </div>
  </article>;
}

